import { useState } from "react";
import type {
  CatalogProductOption,
  CatalogProductVariant,
} from "@babackapp/private-api-client/client/use-catalog-product";

function findVariant(
  variants: CatalogProductVariant[],
  selectedOptions: Record<string, string>,
): CatalogProductVariant | undefined {
  return variants.find((v) =>
    v.selectedOptions.every((o) => selectedOptions[o.name] === o.value),
  );
}

function firstVariantOptions(
  variant: CatalogProductVariant | undefined,
): Record<string, string> {
  if (!variant) return {};
  return Object.fromEntries(variant.selectedOptions.map((o) => [o.name, o.value]));
}

export function getUniqueValues(
  variants: CatalogProductVariant[],
  optionName: string,
): string[] {
  const seen = new Set<string>();
  const values: string[] = [];
  for (const v of variants) {
    const opt = v.selectedOptions.find((o) => o.name === optionName);
    if (opt && !seen.has(opt.value)) {
      seen.add(opt.value);
      values.push(opt.value);
    }
  }
  return values;
}

export function useProductVariantPicker(
  options: CatalogProductOption[],
  variants: CatalogProductVariant[],
) {
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>(
    () => firstVariantOptions(variants[0]),
  );

  const selectableOptions = options.filter(
    (opt) => getUniqueValues(variants, opt.name).length > 1,
  );

  const selectedVariant = findVariant(variants, selectedOptions) ?? null;

  function handleOptionChange(name: string, value: string) {
    setSelectedOptions((prev) => ({ ...prev, [name]: value }));
  }

  return { selectedOptions, selectedVariant, selectableOptions, handleOptionChange };
}
